import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import type { ConversationSummary, ConversationStatus } from '@/api/types';
import { cn } from '@/lib/utils';

interface ConversationListItemProps {
  conversation: ConversationSummary;
}

const statusColors: Record<ConversationStatus, string> = {
  open: 'bg-green-500',
  pending: 'bg-amber-500',
  resolved: 'bg-blue-500',
  closed: 'bg-gray-400',
};

export function ConversationListItem({ conversation }: ConversationListItemProps) {
  const timeAgo = conversation.last_message_at
    ? formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: false })
    : '';

  const isUnassigned = !conversation.assigned_operator_id;

  return (
    <Link
      to={`/conversation/${conversation.id}`}
      className={cn(
        'flex items-center gap-3 px-4 py-2 hover:bg-gray-50 border-b border-gray-100 transition-colors',
        isUnassigned && 'bg-amber-50/50'
      )}
    >
      {/* Status indicator */}
      <div className={cn('w-2 h-2 rounded-full flex-shrink-0', statusColors[conversation.status])} />

      {/* Content */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        {/* Title and meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              #{conversation.id.slice(0, 8)}
            </span>
            {conversation.subject && (
              <span className="text-sm text-gray-600 truncate hidden sm:inline">
                {conversation.subject}
              </span>
            )}
          </div>
        </div>

        {/* Message count */}
        <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
          {conversation.message_count}
        </span>

        {/* Assignment indicator */}
        {isUnassigned ? (
          <span className="text-xs text-amber-600 flex-shrink-0 font-medium">Unassigned</span>
        ) : (
          <span className="w-5 h-5 bg-slate-200 rounded-full flex-shrink-0" title="Assigned" />
        )}

        {/* Time */}
        <span className="text-xs text-gray-400 w-12 text-right flex-shrink-0 tabular-nums">
          {timeAgo}
        </span>
      </div>
    </Link>
  );
}
